import React, { useState, useEffect, useContext, useRef, useMemo, useCallback } from 'react';
import moment from 'moment';
import Swal from 'sweetalert2';
import { AuthContext } from '../contexts/auth';
import Fetching from "./fetching";
import OuSelectionPair from './modal_select_OU_to_pair';
import CalendarMonthSelect from './calendar_month_select';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Collapse, IconButton, Box, Typography
} from '@mui/material';

import {
  getJustificationRequestsCompanyList,
  updateJustificationRequestStatus,
  deleteJustificationRequest,
  getJustificationRequestsSummary
} from '../services/api_justification_requests';
import { listOrganizationalUnits } from '../services/api_org_unit';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon   from '@mui/icons-material/KeyboardArrowUp';
import ModeEditOutlinedIcon  from '@mui/icons-material/ModeEditOutlined';
import SaveOutlinedIcon      from '@mui/icons-material/SaveOutlined';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';

import CustomTooltipComponent from './custom_tooltip_component';

import XCircleIcon from '@heroicons/react/24/outline/esm/XCircleIcon';
import CheckCircleIcon from '@heroicons/react/24/outline/esm/CheckCircleIcon';
import { ChevronDownIcon, ChevronUpIcon, ChevronLeftIcon, ChevronRightIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import SchedulerContextBar from './scheduler_context_bar';

// --- util ---
function formatJustificationEnd(datetimeFrom, datetimeTo) {
    const from = moment(datetimeFrom);
    const to = moment(datetimeTo);
    const isNextDayMidnight =
        to.isSame(from.clone().add(1, 'days').startOf('day'), 'day') && to.format('HH:mm') === '00:00';
    return {
        date: isNextDayMidnight ? from.format('DD/MM/YYYY') : to.format('DD/MM/YYYY'),
        time: isNextDayMidnight ? '24:00' : to.format('HH:mm'),
    };
}

const EMP_PAGE_SIZE = 25;
const HEADER_OFFSET = 80;

const GiustificativiRichiesteNoNull = () => {
    const { token } = useContext(AuthContext);

    // --- SUMMARY (righe principali) ---
    const [groups, setGroups] = useState([]);
    const [page, setPage] = useState(1);              // pagina summary
    const [pageSize, setPageSize] = useState(25);
    const [totalPages, setTotalPages] = useState(1);

    // --- DETTAGLI per dipendente ---
    const [employeeItems, setEmployeeItems] = useState({});

    // --- FILTRI ---
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState(new Date());
    const [calendarVisible, setCalendarVisible] = useState(false);
    const [fromDate, setFromDate] = useState(moment(new Date()).startOf('month').format('YYYY-MM-DD'));
    const [toDate, setToDate]     = useState(moment(new Date()).endOf('month').format('YYYY-MM-DD'));

    // ⬇️ chiave stabile per i dipendenti selezionati (mancava, causava l’errore)
    const employeesKey = useMemo(
        () => selectedEmployees.map(e => e.id).sort((a,b) => a - b).join(','),
        [selectedEmployees]
    );

    // --- UO ---
    const [units, setUnits] = useState([]);
    const [selectedUnit, setSelectedUnit] = useState(null);
    const [isUnitsLoading, setIsUnitsLoading] = useState(false);

    // --- UI ---
    const [isLoading, setIsLoading] = useState(false); // loading summary
    const [isError, setIsError]     = useState(false);
    const [openGroups, setOpenGroups] = useState(() => new Set());

    // --- Modal OU & note ---
    const [showUnitSelection, setShowUnitSelection] = useState(false);
    const [selectedJustification, setSelectedJustification] = useState(null);
    const [editingNoteId, setEditingNoteId] = useState(null);
    const [editingNoteText, setEditingNoteText] = useState('');

    // --- Abort controllers ---
    const summaryCtrlRef = useRef(null);
    const detailCtrlsRef = useRef(new Map());

    // Anchor per scroll all'inizio lista del dipendente
    const anchorRefs = useRef(new Map());
    const setEmpAnchorRef = (empId) => (el) => {
        if (el) anchorRefs.current.set(empId, el);
        else anchorRefs.current.delete(empId);
    };
    const scrollToEmpTop = (empId, offset = HEADER_OFFSET) => {
        const el = anchorRefs.current.get(empId);
        if (!el) return;
        const y = el.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top: y, behavior: 'smooth' });
    };

    // --- Abort helpers ---
    const abortDetail = (empId) => {
        const ctrl = detailCtrlsRef.current.get(empId);
        if (ctrl) {
        try { ctrl.abort(); } catch {}
        detailCtrlsRef.current.delete(empId);
        }
    };
    const abortAllDetails = () => {
        for (const ctrl of detailCtrlsRef.current.values()) {
        try { ctrl.abort(); } catch {}
        }
        detailCtrlsRef.current.clear();
    };

    // Caricamento UO
    useEffect(() => {
        const fetchUnits = async () => {
        setIsUnitsLoading(true);
        setIsError(false);
        try {
            const response = await listOrganizationalUnits(token, true, null, 'justifications');
            setUnits(response.data || []);
        } catch (error) {
            console.error('Error fetching units:', error);
            if (error?.response?.status !== 402) setIsError(true);
        } finally {
            setIsUnitsLoading(false);
        }
        };
        fetchUnits();
    }, [token]);

    // Reset coerenti
    const handleResetUnit = () => {
        setSelectedUnit(null);
        if (selectedEmployees.length) setSelectedEmployees([]);
        setPage(1);
    };
    const handleResetEmployees = () => {
        setSelectedEmployees([]);
        setPage(1);
    };

    // Handler passati alla top bar
    const handleSelectUnit = (unit) => {
        setSelectedUnit(unit || null);
        setSelectedEmployees([]);
        setEmployeeItems({});
        setOpenGroups(new Set());
        setPage(1);
    };

    const handleEmployeesChange = (list) => {
        setSelectedEmployees(Array.isArray(list) ? list : []);
        setPage(1);
    };

    const handlePageSizeChange = (size) => {
        setPageSize(size);
        setPage(1);
    };

    // Deriva date dal mese selezionato
    useEffect(() => {
        setFromDate(moment(selectedMonth).startOf('month').format('YYYY-MM-DD'));
        setToDate(moment(selectedMonth).endOf('month').format('YYYY-MM-DD'));
    }, [selectedMonth]);

    // Reset forte quando cambia dataset (mese/OU/dipendenti)
    useEffect(() => {
        if (summaryCtrlRef.current) { try { summaryCtrlRef.current.abort(); } catch {} }
        abortAllDetails();
        setOpenGroups(new Set());
        setEmployeeItems({});
        setGroups([]);
        setPage(1);
    }, [fromDate, toDate, selectedUnit?.id, employeesKey]);

    // Fetch SUMMARY (no_null)
    useEffect(() => {
        const ctrl = new AbortController();
        summaryCtrlRef.current = ctrl;

        const loadSummary = async () => {
        setIsLoading(true);
        setIsError(false);
        try {
            const res = await getJustificationRequestsSummary(
            token,
            fromDate,
            toDate,
            'no_null',
            selectedUnit?.id || undefined,
            selectedEmployees.map(e => e.id),
            page,
            pageSize,
            ctrl.signal,
            'justifications'
            );

            const results = Array.isArray(res?.results) ? res.results : [];
            const normalized = results.map(r => ({
                key: r?.employee?.id ?? r?.employee?.cf,
                employee: r?.employee,
                total_requests: r?.total_requests ?? 0,
                approved_count: r?.approved_count ?? 0,
                rejected_count: r?.rejected_count ?? 0,
            }));
            setGroups(normalized);
            const count = Number(res?.count ?? normalized.length);
            setTotalPages(Math.max(1, Math.ceil(count / pageSize)));

            // Mantieni aperte solo le righe ancora visibili
            setOpenGroups(prev => {
            const visible = new Set(normalized.map(g => g?.employee?.id ?? g.key));
            const next = new Set();
            prev.forEach(k => { if (visible.has(k)) next.add(k); });
            return next;
            });
        } catch (err) {
            if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') {
            console.error('Summary error:', err);
            setIsError(true);
            }
        } finally {
            if (summaryCtrlRef.current === ctrl) setIsLoading(false);
        }
        };

        loadSummary();
        return () => { try { ctrl.abort(); } catch {} };
    }, [token, fromDate, toDate, selectedUnit?.id, employeesKey, page, pageSize]);

    // Helpers UI mese
    const handlePreviousMonth = () =>
        setSelectedMonth(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
    const handleNextMonth = () =>
        setSelectedMonth(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));

    // Memo utili
    const isGroupOpen = (employeeId) => openGroups.has(employeeId);
    const hasGroups = !isLoading && groups.length > 0;
    const allOpen = useMemo(
        () => hasGroups && groups.every(g => openGroups.has(g.employee?.id ?? g.key)),
        [hasGroups, groups, openGroups]
    );

    // Toggle righe
    const fetchEmployeeRequests = useCallback(async (empId, pageEmp = 1) => {
        abortDetail(empId);
        const ctrl = new AbortController();
        detailCtrlsRef.current.set(empId, ctrl);

        setEmployeeItems(prev => ({
        ...prev,
        [empId]: { ...(prev[empId] || {}), isLoading: true, error: null }
        }));

        try {
            const res = await getJustificationRequestsCompanyList(
                token,
                fromDate,
                pageEmp,
                EMP_PAGE_SIZE,
                'no_null',
                selectedUnit?.id,
                [empId],
                toDate,
                ctrl.signal,
                'justifications'
            );

            const items = Array.isArray(res?.results) ? res.results : [];
            const count = Number(res?.count ?? items.length);
            const totalPagesEmp = Math.max(1, Math.ceil(count / EMP_PAGE_SIZE));

            if (items.length === 0 && pageEmp > 1 && pageEmp > totalPagesEmp) {
                const fallback = totalPagesEmp;
                const res2 = await getJustificationRequestsCompanyList(
                    token,
                    fromDate,
                    fallback,
                    EMP_PAGE_SIZE,
                    'no_null',
                    selectedUnit?.id,
                    [empId],
                    toDate,
                    ctrl.signal,
                    'justifications'
                );
                const items2 = Array.isArray(res2?.results) ? res2.results : [];
                const count2 = Number(res2?.count ?? items2.length);
                const total2 = Math.max(1, Math.ceil(count2 / EMP_PAGE_SIZE));
                setEmployeeItems(prev => ({
                    ...prev,
                    [empId]: { items: items2, isLoading: false, error: null, page: fallback, totalPages: total2, count: count2 }
                }));
                return;
            }

            setEmployeeItems(prev => ({
                ...prev,
                [empId]: { items, isLoading: false, error: null, page: pageEmp, totalPages: totalPagesEmp, count }
            }));
        } catch (e) {
            if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') {
                setEmployeeItems(prev => ({
                ...prev,
                [empId]: { ...(prev[empId] || {}), isLoading: false }
                }));
            } else {
                console.error('Dettaglio dipendente error:', e);
                setEmployeeItems(prev => ({
                ...prev,
                [empId]: { ...(prev[empId] || {}), isLoading: false, error: e?.message || 'Errore' }
                }));
            }
        } finally {
            const current = detailCtrlsRef.current.get(empId);
            if (current === ctrl) detailCtrlsRef.current.delete(empId);
        }
    }, [token, fromDate, toDate, selectedUnit?.id]);

    const toggleGroup = (employeeId) => {
        abortDetail(employeeId);
        setOpenGroups(prev => {
        const n = new Set(prev);
        if (n.has(employeeId)) {
            n.delete(employeeId);
        } else {
            n.add(employeeId);
            if (!employeeItems[employeeId]?.items) fetchEmployeeRequests(employeeId, 1);
        }
        return n;
        });
    };

    const toggleAllGroups = () => {
        if (!hasGroups) return;
            const areAllOpen = groups.every(g => openGroups.has(g.employee?.id ?? g.key));
        if (areAllOpen) {
            abortAllDetails();
            setOpenGroups(new Set());
            return;
        }
        abortAllDetails();
        const next = new Set(openGroups);
        groups.forEach(g => {
        const empId = g.employee?.id ?? g.key;
        next.add(empId);
        if (!employeeItems[empId]?.items) fetchEmployeeRequests(empId, 1);
        });
        setOpenGroups(next);
    };

    // Navigazione per-dipendente
    const gotoEmpPage = (empId, target) => {
        const cur = employeeItems[empId];
        if (!cur) return;
        const total = cur.totalPages || 1;
        const pageTarget = Math.min(total, Math.max(1, target));
        if (pageTarget !== cur.page) {
            abortDetail(empId);
            fetchEmployeeRequests(empId, pageTarget);
            scrollToEmpTop(empId);
        }
    };
    const handleEmpPrev = (empId) => gotoEmpPage(empId, (employeeItems[empId]?.page || 1) - 1);
    const handleEmpNext = (empId) => gotoEmpPage(empId, (employeeItems[empId]?.page || 1) + 1);

    // Aggiorna contatori summary
    const bumpSummary = (empId, { approvedDelta = 0, rejectedDelta = 0, totalDelta = 0 }) => {
        setGroups(prev => prev
            .map(g => {
            const gid = g.employee?.id ?? g.key;
            if (gid !== empId) return g;
            return {
                ...g,
                total_requests: Math.max(0, (g.total_requests ?? 0) + totalDelta),
                approved_count: Math.max(0, (g.approved_count ?? 0) + approvedDelta),
                rejected_count: Math.max(0, (g.rejected_count ?? 0) + rejectedDelta),
            };
            })
            .filter(g => (g.total_requests ?? 0) > 0)
        );
    };

    // Azioni
    const handleUpdateRequest = async (request) => {
        const currentDate = new Date();
        const oldStatus  = request.status;
        const newStatus  = oldStatus === 'approved' ? 'rejected' : 'approved';
        const empId      = request?.employee?.id;

        const { isConfirmed } = await Swal.fire({
            title: 'Conferma Modifica',
            html: `
                <div class="mt-2">
                    <p class="text-gray-500">
                    La richiesta del dipendente ${request.employee.last_name ?
                        `${request.employee.last_name} ${request.employee.first_name}` :
                        request.employee.cf || ''} è stata ${oldStatus === 'approved'
                        ? '<span class="text-green-500">Approvata</span>'
                        : '<span class="text-red-500">Rifiutata</span>'} 
                    il ${moment(request.reviewed_on).format('DD/MM/YYYY, HH:mm')}
                    ${request.reviewed_by?.first_name || request.reviewed_by?.last_name ? 
                        ` da ${request.reviewed_by.first_name} ${request.reviewed_by.last_name}.` : ''} 
                    <br />Vuoi modificare?
                    </p>
                </div>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: newStatus === 'approved' ? 'Approva Richiesta' : 'Rifiuta Richiesta',
            cancelButtonText: 'Annulla',
            reverseButtons: true
        });
        if (!isConfirmed) return;

        try {
            await updateJustificationRequestStatus(token, request.id, newStatus, currentDate);

            setEmployeeItems(prev => {
            const cur = prev[empId];
            if (!cur) return prev;
            const items = (cur.items || []).map(r =>
                r.id === request.id ? { ...r, status: newStatus, reviewed_on: currentDate } : r
            );
            return { ...prev, [empId]: { ...cur, items } };
            });

            const approvedDelta = (newStatus === 'approved' ? 1 : 0) - (oldStatus === 'approved' ? 1 : 0);
            const rejectedDelta = (newStatus === 'rejected' ? 1 : 0) - (oldStatus === 'rejected' ? 1 : 0);
            bumpSummary(empId, { approvedDelta, rejectedDelta, totalDelta: 0 });

            Swal.fire({ title: 'Salvato!', text: `Stato modificato in "${newStatus === 'approved' ? 'Approvata' : 'Rifiutata'}"`, icon: 'success', timer: 1200, showConfirmButton: false });
        } catch (error) {
            console.error('Error updating justification request status:', error);
            const details = error.response?.data ? Object.values(error.response.data).flat() : [];
            const errorMessage = details.length ? details.join('\n') : 'Errore nell\'aggiornamento della richiesta.';
            Swal.fire({ icon: 'error', title: 'Oops...', text: errorMessage });
        }
    };

    const handleDeleteRequest = async (request) => {
        const empId = request?.employee?.id;
        const { isConfirmed } = await Swal.fire({
            title: 'Conferma Eliminazione',
            html: `
            <div class="mt-2">
                <p class="text-gray-500">
                Sei sicuro di voler eliminare la richiesta di giustificativo del dipendente 
                ${request.employee.last_name ? `${request.employee.last_name} ${request.employee.first_name}` : request.employee.cf || ''}?
                </p>
            </div>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sì, elimina',
            cancelButtonText: 'Annulla',
            reverseButtons: true
        });
        if (!isConfirmed) return;

        try {
            Swal.fire({ title: 'Eliminazione in corso...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
            await deleteJustificationRequest(token, request.id);

            const wasApproved = request.status === 'approved';
            bumpSummary(empId, {
                approvedDelta: wasApproved ? -1 : 0,
                rejectedDelta: wasApproved ? 0 : -1,
                totalDelta: -1
            });

            const curPage = employeeItems[empId]?.page || 1;
            abortDetail(empId);
            await fetchEmployeeRequests(empId, curPage);

            Swal.close();
            Swal.fire({ title: 'Eliminato!', text: 'La richiesta di giustificativo è stata eliminata.', icon: 'success', timer: 1200, showConfirmButton: false });
        } catch (error) {
            console.error('Error deleting justification request:', error);
            Swal.close();
            Swal.fire({ icon: 'error', title: 'Oops...', text: 'Errore durante l\'eliminazione della richiesta.' });
        }
    };

    const handleSelectOUtoPair = async (selectedUnitToPair) => {
        if (!selectedJustification || !selectedUnitToPair) return;
        const empId = selectedJustification?.employee?.id;
        try {
            const updated = await updateJustificationRequestStatus(
                token,
                selectedJustification.id,
                undefined, undefined, undefined, undefined, undefined,
                selectedUnitToPair.id
            );
            setEmployeeItems(prev => {
                const cur = prev[empId] || { items: [] };
                const nextItems = (cur.items || []).map(r => r.id === selectedJustification.id ? { ...r, ...updated } : r);
                return { ...prev, [empId]: { ...cur, items: nextItems } };
            });
            Swal.fire({ title: 'Salvato!', icon: 'success', timer: 1200, showConfirmButton: false });
        } catch (error) {
            console.error('Error updating OU:', error);
            let msg = 'Si è verificato un errore nell\'aggiornamento.';
            if (error.response?.data) {
                const em = Object.values(error.response.data).flat().join(', ');
                if (em) msg = em;
            }
            Swal.fire({ title: 'Errore!', text: msg, icon: 'error', confirmButtonText: 'Chiudi' });
        }
    };

    const handleDeletePairedUnit = async (justificationId, empId) => {
        if (!justificationId) return;
        try {
            await updateJustificationRequestStatus(token, justificationId, undefined, undefined, undefined, undefined, undefined, null);
            setEmployeeItems(prev => {
                const cur = prev[empId] || { items: [] };
                const nextItems = cur.items.map(r => (r.id === justificationId ? { ...r, ou: null, ou_name: null } : r));
                return { ...prev, [empId]: { ...cur, items: nextItems } };
            });
            Swal.fire({ title: 'Salvato!', icon: 'success', timer: 1200, showConfirmButton: false });
        } catch (error) {
            console.error('Errore durante l\'eliminazione OU:', error);
            Swal.fire({ title: 'Errore!', text: 'Si è verificato un errore durante l\'eliminazione dell\'unità associata.', icon: 'error', confirmButtonText: 'Chiudi' });
        }
    };

    // NOTE
    const handleSaveNote = async (justificationId, empId) => {
        if (!editingNoteText.trim()) return;
        try {
            Swal.fire({ title: 'Salvataggio in corso...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
            await updateJustificationRequestStatus(
                token,
                justificationId,
                undefined, undefined, undefined, undefined,
                editingNoteText,
                undefined
            );
            setEmployeeItems(prev => {
                const cur = prev[empId] || { items: [] };
                const nextItems = cur.items.map(r => (r.id === justificationId ? { ...r, note: editingNoteText } : r));
                return { ...prev, [empId]: { ...cur, items: nextItems } };
            });
            setEditingNoteId(null);
            setEditingNoteText('');
            Swal.close();
            Swal.fire({ title: 'Salvato!', icon: 'success', timer: 1200, showConfirmButton: false });
        } catch (error) {
            Swal.close();
            let backendMsg = 'Errore durante il salvataggio della nota.';
            if (error?.response?.data) {
                const data = error.response.data;
                if (Array.isArray(data)) backendMsg = data.join(' ');
                else if (typeof data === 'object') {
                const msgs = Object.values(data).flat().filter(Boolean);
                if (msgs.length) backendMsg = msgs.join(' ');
                } else if (typeof data === 'string') backendMsg = data;
            }
            Swal.fire({ title: 'Errore!', text: backendMsg, icon: 'error', confirmButtonText: 'Chiudi' });
        }
    };

    const handleDeleteNote = async (justificationId, empId) => {
        try {
            Swal.fire({ title: 'Eliminazione nota...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
            await updateJustificationRequestStatus(
                token,
                justificationId,
                undefined, undefined, undefined, undefined,
                '',
                undefined
            );
            setEmployeeItems(prev => {
                const cur = prev[empId] || { items: [] };
                const nextItems = cur.items.map(r => (r.id === justificationId ? { ...r, note: '' } : r));
                return { ...prev, [empId]: { ...cur, items: nextItems } };
            });
            setEditingNoteId(null);
            setEditingNoteText('');
            Swal.close();
            Swal.fire({ title: 'Eliminata!', icon: 'success', timer: 1200, showConfirmButton: false });
        } catch (error) {
            Swal.close();
            Swal.fire({ title: 'Errore!', text: 'Impossibile eliminare la nota.', icon: 'error', confirmButtonText: 'Chiudi' });
        }
    };

    // Righe di dettaglio
    const JustificationRow = ({ item }) => {
        const { time: endTime } = formatJustificationEnd(item.datetime_from, item.datetime_to);
        const hasOU = Boolean(item?.ou);
        const empId = item?.employee?.id;
        const isEditingThis = editingNoteId === item.id;

        return (
            <TableRow className="border-b border-gray-200">
                <TableCell sx={{ width: '14%' }}>
                    <span className="font-semibold">{moment(item.datetime_from).format('DD/MM/YYYY')}</span>
                </TableCell>

                <TableCell sx={{ width: '46%' }}>
                    <div className="rounded-lg overflow-hidden border border-gray-200 shadow-sm px-3 py-2 bg-white">
                        <div className="text-indigo-700 font-semibold uppercase tracking-wide">
                            {item.justification?.justification?.description}
                        </div>

                        {moment(item.datetime_from).format('HH:mm:ss') === '00:00:00' &&
                        moment(item.datetime_to).format('HH:mm:ss') === '23:59:59' ? (
                            <div className="text-gray-600 text-sm">
                                dal <span className="font-semibold">{moment(item.datetime_from).format('DD/MM/YYYY')}</span>{' '}
                                al <span className="font-semibold">{moment(item.datetime_to).format('DD/MM/YYYY')}</span>
                            </div>
                        ) : (
                            <div className="text-gray-600 text-sm">
                                Dalle <span className="font-semibold">{moment(item.datetime_from).format('HH:mm')}</span>{' '}
                                alle <span className="font-semibold">{endTime}</span>
                            </div>
                        )}

                        <div className="text-sm text-gray-700">
                            <div className="grid grid-cols-[auto,1fr,auto] items-start gap-2">
                                <span className="font-semibold text-indigo-700 pt-0.5">Note:</span>

                                {isEditingThis ? (
                                    <input
                                        type="text"
                                        className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                                        value={editingNoteText}
                                        onChange={(e) => setEditingNoteText(e.target.value)}
                                        autoFocus
                                    />
                                ) : item.note?.trim() ? (
                                    <span className="text-gray-800 leading-5 whitespace-pre-line break-words">
                                        {item.note}
                                    </span>
                                ) : (
                                    <button
                                        className="text-gray-400 hover:text-indigo-700 italic text-left p-0"
                                        onClick={() => { setEditingNoteId(item.id); setEditingNoteText(''); }}
                                    >
                                        Non presenti. Clicca per aggiungere
                                    </button>
                                )}

                                <div className="flex items-center gap-1 shrink-0 self-start">
                                    {isEditingThis ? (
                                        <>
                                            <CustomTooltipComponent title="Salva nota" placement="top" arrow>
                                                <button
                                                    className="bg-indigo-500 hover:bg-indigo-600 px-1.5 py-1 rounded-md shadow text-white transition"
                                                    onClick={() => handleSaveNote(item.id, empId)}
                                                >
                                                <SaveOutlinedIcon sx={{ fontSize: 16 }} />
                                                </button>
                                            </CustomTooltipComponent>
                                            <CustomTooltipComponent title="Annulla" placement="top" arrow>
                                                <button
                                                    className="bg-gray-300 hover:bg-gray-400 px-1.5 py-1 rounded-md shadow text-white transition"
                                                    onClick={() => { setEditingNoteId(null); setEditingNoteText(''); }}
                                                >
                                                    <RestartAltOutlinedIcon sx={{ fontSize: 16 }} />
                                                </button>
                                            </CustomTooltipComponent>
                                        </>
                                    ) : (
                                        item.note?.trim() && (
                                            <>
                                                <CustomTooltipComponent title="Modifica nota" placement="top" arrow>
                                                    <button
                                                        className="p-1 rounded-full bg-white border border-gray-300 hover:bg-indigo-50 hover:border-indigo-200 hover:text-indigo-700 transition"
                                                        onClick={() => { setEditingNoteId(item.id); setEditingNoteText(item.note || ''); }}
                                                    >
                                                        <PencilSquareIcon className="h-4 w-4" />
                                                    </button>
                                                </CustomTooltipComponent>
                                                <CustomTooltipComponent title="Elimina nota" placement="top" arrow>
                                                    <button
                                                        className="p-1 rounded-full bg-white border border-gray-300 hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition"
                                                        onClick={() => handleDeleteNote(item.id, empId)}
                                                    >
                                                        <TrashIcon className="h-4 w-4" />
                                                    </button>
                                                </CustomTooltipComponent>
                                            </>
                                        )
                                    )}
                                </div>
                            </div>
                        </div>

                        {hasOU ? (
                            <div className="mt-2 -mx-3 -mb-2 px-3 py-2 rounded-md bg-indigo-50 text-sm text-gray-700 flex items-center gap-2">
                                <span className="font-semibold">Unità associata:</span> {item.ou_name}
                                <button
                                    className="p-1 rounded-full hover:bg-red-50 text-gray-600 hover:text-red-600 transition"
                                    onClick={() => handleDeletePairedUnit(item.id, empId)}
                                    title="Rimuovi associazione"
                                >
                                    <TrashIcon className="h-5 w-5" />
                                </button>
                            </div>
                        ) : (
                            <div className="mt-2">
                                <button
                                    className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 ring-1 ring-inset ring-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 transition"
                                    onClick={() => { setSelectedJustification(item); setShowUnitSelection(true); }}
                                >
                                    Associa Unità
                                </button>
                            </div>
                        )}
                    </div>
                </TableCell>

                <TableCell sx={{ width: '10%' }} align="center">
                    {item.status === 'approved'
                        ? <CheckCircleIcon className="w-5 h-5 text-teal-500 inline-block" />
                        : <XCircleIcon className="w-5 h-5 text-red-500 inline-block" />
                    }
                </TableCell>

                <TableCell sx={{ width: '30%' }} align="center">
                    <div className="flex flex-wrap justify-center gap-2">
                        <CustomTooltipComponent title="Modifica status" placement="top" arrow>
                            <button
                                onClick={() => handleUpdateRequest(item)}
                                className="p-2 rounded-full bg-white text-gray-700 border border-gray-300
                                        hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200 shadow-sm transition"
                            >
                                <ModeEditOutlinedIcon sx={{ fontSize: 18 }} />
                            </button>
                        </CustomTooltipComponent>

                        <CustomTooltipComponent title="Elimina richiesta" placement="top" arrow>
                            <button
                                onClick={() => handleDeleteRequest(item)}
                                className="p-2 rounded-full bg-white text-gray-700 border border-gray-300
                                        hover:bg-red-50 hover:text-red-600 hover:border-red-300 shadow-sm transition"
                            >
                                <TrashIcon className="h-5 w-5" />
                            </button>
                        </CustomTooltipComponent>
                    </div>
                </TableCell>
            </TableRow>
        );
    };

    const GroupRow = ({ group }) => {
        const fullName = group.employee?.first_name
        ? `${group.employee.last_name} ${group.employee.first_name}`
        : group.employee?.cf;
        const empId = group.employee?.id ?? group.key;
        const open = isGroupOpen(empId);

        const detail = employeeItems[empId];
        const isDetailLoading = detail?.isLoading;
        const detailItems = detail?.items || [];
        const empPage = detail?.page || 1;
        const empTotalPages = detail?.totalPages || 1;

        return (
        <>
            <TableRow
                onClick={() => toggleGroup(empId)}
                className="hover:bg-gray-50 cursor-pointer transition"
                aria-expanded={open}
                hover
            >
                <TableCell sx={{ width: 56 }} onClick={(e) => { e.stopPropagation(); toggleGroup(empId); }}>
                    <IconButton aria-label="expand row" size="small">
                    {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell>{fullName}</TableCell>
                <TableCell align="center">{group.total_requests ?? 0}</TableCell>
                <TableCell align="center">{group.approved_count ?? 0}</TableCell>
                <TableCell align="center">{group.rejected_count ?? 0}</TableCell>
                <TableCell />
            </TableRow>

            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6} className="bg-indigo-50">
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2 }}>
                            <div ref={setEmpAnchorRef(empId)} />
                                <Typography variant="p" gutterBottom component="div" className='font-semibold text-indigo-800 uppercase'>
                                Richieste di {fullName}
                                </Typography>

                                {isDetailLoading ? (
                            <div className="py-4"><Fetching /></div>
                            ) : (
                                <>
                                    <Table size="small" aria-label="richieste" className="rounded-lg">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell style={{ width: '14%' }}>Data</TableCell>
                                                <TableCell style={{ width: '46%' }}>Dettagli</TableCell>
                                                <TableCell style={{ width: '10%' }} align="center">Status</TableCell>
                                                <TableCell style={{ width: '30%' }} align="center">Azioni</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody className="bg-gray-50 shadow-lg">
                                            {detailItems.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={4} align="center" className="py-6 italic text-sm text-gray-500">
                                                    Nessuna richiesta presente per questo periodo
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                detailItems
                                                    .slice()
                                                    .sort((a, b) => {
                                                    const ta = moment(a?.datetime_from).valueOf();
                                                    const tb = moment(b?.datetime_from).valueOf();
                                                    if (ta !== tb) return ta - tb;  // più vecchie prima
                                                    return a.id - b.id;
                                                    })
                                                    .map(item => (
                                                        <JustificationRow key={item.id} item={item} />
                                                    ))
                                            )}
                                        </TableBody>
                                    </Table>

                                    {detail?.count > EMP_PAGE_SIZE && (
                                        <div className="flex justify-between mt-4 bg-gray-50 p-2 shadow-md border border-gray-200 rounded-md">
                                            <button
                                                className="inline-flex items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                                                onClick={() => handleEmpPrev(empId)}
                                                disabled={empPage === 1}
                                            >
                                                Precedente
                                            </button>
                                            <span className='text-sm flex items-center text-gray-900'>Pagina {empPage} di {empTotalPages}</span>
                                            <button
                                                className="inline-flex items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                                                onClick={() => handleEmpNext(empId)}
                                                disabled={empPage === empTotalPages}
                                            >
                                                Successivo
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
        );
    };

    // Render
    return (
        <div className="min-h-full">
            <div className="mx-auto w-full max-w-[1600px] px-4 md:px-6">
                <div>
                    <SchedulerContextBar
                        className="w-full"
                        units={units}
                        selectedUnit={selectedUnit}
                        onSelectUnit={handleSelectUnit}
                        onResetUnit={handleResetUnit}
                        isUnitsLoading={isUnitsLoading}
                        token={token}
                        selectedEmployees={selectedEmployees}
                        onEmployeesChange={handleEmployeesChange}
                        pageSize={pageSize}
                        onPageSizeChange={handlePageSizeChange}
                        contractStartDate={fromDate}
                        contractEndDate={toDate}
                    />
                </div>

                {/* Top bar: Toggle all */}
                <div>
                    <div className="px-3 py-3 w-full bg-white" style={{ position: 'relative', zIndex: 11 }}>
                        <div className="flex items-center justify-end">
                            {hasGroups ? (
                                <button
                                    onClick={toggleAllGroups}
                                    className="inline-flex items-center gap-2 text-sm px-4 py-1 rounded-md font-semibold whitespace-nowrap
                                            bg-indigo-600 text-white hover:bg-indigo-500 transition"
                                >
                                    {allOpen ? 'Chiudi tutte' : 'Apri tutte le righe'}
                                    {allOpen ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                                </button>
                            ) : (
                                <div className="h-8"></div>
                            )}
                        </div>
                    </div>

                    {/* Selettore mese */}
                    <div className="grid grid-cols-1 w-full pt-4 border border-gray-200 rounded-md">
                        <div
                            style={{ flex: '1 1 auto', width: 'auto' }}
                            className="pb-5 pt-2 px-4 border-l border-r border-gray-200 text-center w-24 text-xs font-semibold text-gray-600 uppercase align-middle"
                        >
                            <div className="grid md:grid-cols-5 grid-cols-3">
                            <div className="col-span-1 p-2 flex justify-end me-3">
                                <button className="text-gray-700 font-semibold px-1 hover:bg-gray-100" onClick={handlePreviousMonth}>
                                <ChevronLeftIcon className="w-5 h-5 cursor-pointer" />
                                </button>
                            </div>
                            <div className="col-span-1 p-2 flex md:justify-end me-3">Seleziona Mese</div>
                            <div className="col-span-1 p-2 justify-start md:hidden block">
                                <button className="text-gray-700 font-semibold px-1 hover:bg-gray-100" onClick={handleNextMonth}>
                                <ChevronRightIcon className="w-5 h-5 cursor-pointer" />
                                </button>
                            </div>
                            <div
                                onClick={() => setCalendarVisible(v => !v)}
                                className="col-span-3 md:col-span-2 border border-gray-300 rounded-md shadow-md p-2 pl-4 flex items-center md:w-4/5 w-full justify-between cursor-pointer"
                            >
                                <div>
                                    {selectedMonth.toLocaleDateString('it-IT', { month: 'long' })}, {selectedMonth.getFullYear()}
                                </div>
                                {calendarVisible
                                    ? <ChevronUpIcon className="h-4 w-4 bg-indigo-100 rounded-md" />
                                    : <ChevronDownIcon className="h-4 w-4 bg-indigo-100 rounded-md" />}
                            </div>
                            <div className="col-span-1 p-2 hidden md:block">
                                <button className="text-gray-700 font-semibold px-1 hover:bg-gray-100" onClick={handleNextMonth}>
                                <ChevronRightIcon className="w-5 h-5 cursor-pointer" />
                                </button>
                            </div>
                            </div>

                            {calendarVisible && (
                                <CalendarMonthSelect
                                    selectedMonth={selectedMonth}
                                    onMonthSelect={setSelectedMonth}
                                    onCloseCalendar={() => setCalendarVisible(false)}
                                />
                            )}
                        </div>
                    </div>

                    {/* contenuti */}
                    {isError && 'Error loading data...'}
                    {isLoading && <Fetching />}

                    {!isLoading && !isError && (
                        <TableContainer component={Paper} className="border border-gray-300 mt-3">
                            <Table aria-label="collapsible table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell />
                                        <TableCell>Dipendente</TableCell>
                                        <TableCell align="center">N. Richieste Totali</TableCell>
                                        <TableCell align="center">Approvate</TableCell>
                                        <TableCell align="center">Rifiutate</TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {groups.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" className="py-10 italic text-sm text-gray-500">
                                        Nessuna richiesta presente per questo periodo
                                        </TableCell>
                                    </TableRow>
                                    ) : (
                                    groups.map((group) => (
                                        <GroupRow key={group.employee?.id ?? group.key} group={group} />
                                    ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {totalPages > 1 && (
                        <div className="flex justify-between mt-8">
                            <button
                                className="inline-flex items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                            >
                                Precedente
                            </button>
                            <span className='text-sm font-medium text-gray-900'>Pagina {page} di {totalPages}</span>
                            <button
                                className="inline-flex items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                            >
                                Successivo
                            </button>
                        </div>
                    )}
                </div>

                {showUnitSelection && (
                    <OuSelectionPair
                        onClose={() => setShowUnitSelection(false)}
                        justification={selectedJustification}
                        onSelectOUtoPair={handleSelectOUtoPair}
                    />
                )}
            </div>
        </div>
    );
};

export default GiustificativiRichiesteNoNull;